"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { clearGoogleCookies } from "@/lib/consent/cleanup";
import { updateConsent } from "@/lib/consent/gtag";
import { requiresConsent } from "@/lib/consent/regions";
import { ACCEPT_ALL, type ConsentChoice, REJECT_ALL } from "@/lib/consent/signals";
import { readConsent, readGeo, writeConsent } from "@/lib/consent/state";
interface ConsentContextValue {
  /** False until cookies have been read on the client (no SSR of consent). */
  ready: boolean;
  choice: ConsentChoice | null;
  /** Pre-fill when no choice is saved: mirrors the regional Google default. */
  defaultChoice: ConsentChoice;
  showBanner: boolean;
  preferencesOpen: boolean;
  setPreferencesOpen: (open: boolean) => void;
  openPreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (choice: ConsentChoice) => void;
}
const ConsentContext = createContext<ConsentContextValue | null>(null);
export function ConsentProvider(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const [ready, setReady] = useState(false);
  const [choice, setChoice] = useState(null);
  const [geo, setGeo] = useState(undefined);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setChoice(readConsent());
      setGeo(readGeo());
      setReady(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = next => {
      writeConsent(next);
      updateConsent(next);
      clearGoogleCookies({
        analytics: !next.analytics,
        marketing: !next.marketing
      });
      setChoice(next);
      setPreferencesOpen(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const save = t3;
  const t4 = requiresConsent(geo) ? REJECT_ALL : ACCEPT_ALL;
  let t5;
  if ($[3] !== choice || $[4] !== geo || $[5] !== preferencesOpen || $[6] !== ready) {
    t5 = ready && choice === null && !preferencesOpen && requiresConsent(geo);
    $[3] = choice;
    $[4] = geo;
    $[5] = preferencesOpen;
    $[6] = ready;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setPreferencesOpen(true);
    t7 = () => save(ACCEPT_ALL);
    t8 = () => save(REJECT_ALL);
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== choice || $[12] !== preferencesOpen || $[13] !== ready || $[14] !== t4 || $[15] !== t5) {
    t9 = {
      ready,
      choice,
      defaultChoice: t4,
      showBanner: t5,
      preferencesOpen,
      setPreferencesOpen,
      openPreferences: t6,
      acceptAll: t7,
      rejectAll: t8,
      save
    };
    $[11] = choice;
    $[12] = preferencesOpen;
    $[13] = ready;
    $[14] = t4;
    $[15] = t5;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const value = t9;
  let t10;
  if ($[17] !== children || $[18] !== value) {
    t10 = <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
    $[17] = children;
    $[18] = value;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used inside <ConsentProvider>");
  }
  return ctx;
}